import React, { useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  Download,
  Loader2,
  Play,
  RefreshCw,
  Upload,
  X,
  XCircle,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import lmsStaffService, {
  IntradayBounceStatus,
  getLmsApiErrorMessage,
} from "./lmsStaffApi";

const QUERY_KEY = ["intraday-bounce-status"];

const TaskStatusBadge: React.FC<{
  status: IntradayBounceStatus["last_task_status"];
}> = ({ status }) => {
  if (!status) {
    return <span className="text-sm text-gray-400">—</span>;
  }
  if (status === "PENDING") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
        <Loader2 className="h-3 w-3 animate-spin" />
        Queued…
      </span>
    );
  }
  if (status === "STARTED") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-800">
        <Loader2 className="h-3 w-3 animate-spin" />
        Processing…
      </span>
    );
  }
  if (status === "SUCCESS") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
        <CheckCircle2 className="h-3 w-3" />
        Success
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
      <XCircle className="h-3 w-3" />
      Failed
    </span>
  );
};

const IntraDayBounceView: React.FC = () => {
  const queryClient = useQueryClient();
  const csvRef = useRef<HTMLInputElement>(null);

  const [uploadError, setUploadError] = useState("");
  const [uploadSuccess, setUploadSuccess] = useState("");
  const [isUploading, setIsUploading] = useState(false);

  const [generateError, setGenerateError] = useState("");
  const [generateSuccess, setGenerateSuccess] = useState("");
  const [postGenerateCooldown, setPostGenerateCooldown] = useState(false);

  useEffect(() => {
    if (!postGenerateCooldown) return;
    const timer = setTimeout(() => setPostGenerateCooldown(false), 3000);
    return () => clearTimeout(timer);
  }, [postGenerateCooldown]);

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const response = await lmsStaffService.getIntradayStatus();
      return response.data;
    },
  });

  const uploadMutation = useMutation({
    mutationFn: ({ csvFile }: { csvFile: File }) =>
      lmsStaffService.uploadIntradayCsv(csvFile),
    onSuccess: (response) => {
      setIsUploading(false);
      setUploadError("");
      setUploadSuccess(response.data.message);
      if (csvRef.current) csvRef.current.value = "";
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (err) => {
      setIsUploading(false);
      setUploadSuccess("");
      setUploadError(
        getLmsApiErrorMessage(err, "Upload failed. Please try again."),
      );
    },
  });

  const generateMutation = useMutation({
    mutationFn: () => lmsStaffService.triggerIntradayGenerate(),
    onSuccess: (response) => {
      setGenerateError("");
      setGenerateSuccess(response.data.message);
      setPostGenerateCooldown(true);
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (err) => {
      setGenerateSuccess("");
      setGenerateError(
        getLmsApiErrorMessage(
          err,
          "Failed to start generation. Please try again.",
        ),
      );
    },
  });

  const handleUpload = () => {
    const csvFile = csvRef.current?.files?.[0] ?? null;
    if (!csvFile) {
      setUploadError("Please select a demands CSV file.");
      return;
    }
    setUploadError("");
    setUploadSuccess("");
    setIsUploading(true);
    uploadMutation.mutate({ csvFile });
  };

  const handleGenerate = () => {
    setGenerateError("");
    setGenerateSuccess("");
    generateMutation.mutate();
  };

  const formatTs = (ts: string | null) => {
    if (!ts) return "—";
    return new Date(ts).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const hasCsv = !!data?.csv_last_uploaded_at;
  const isTaskInFlight =
    data?.last_task_status === "PENDING" ||
    data?.last_task_status === "STARTED";

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-900">
            Pipeline Status
          </h3>
          <button
            type="button"
            onClick={() =>
              queryClient.invalidateQueries({ queryKey: QUERY_KEY })
            }
            disabled={isFetching}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw
              className={`h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`}
            />
            Refresh
          </button>
        </div>

        {isLoading && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading…
          </div>
        )}

        {isError && (
          <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            {getLmsApiErrorMessage(error, "Could not load status.")}
          </div>
        )}

        {data && (
          <dl className="space-y-3 text-sm">
            <div className="flex items-center justify-between gap-4">
              <dt className="flex items-center gap-1.5 text-gray-500">
                <Clock className="h-3.5 w-3.5" />
                CSV uploaded
              </dt>
              <dd className="font-medium text-gray-900">
                {formatTs(data.csv_last_uploaded_at)}
              </dd>
            </div>

            <div className="flex items-center justify-between gap-4">
              <dt className="text-gray-500">Task status</dt>
              <dd>
                <TaskStatusBadge status={data.last_task_status} />
              </dd>
            </div>

            <div className="flex items-center justify-between gap-4">
              <dt className="flex items-center gap-1.5 text-gray-500">
                <Clock className="h-3.5 w-3.5" />
                Report generated
              </dt>
              <dd className="font-medium text-gray-900">
                {formatTs(data.xlsx_last_generated_at)}
              </dd>
            </div>

            <div className="border-t border-gray-100 pt-3 flex items-center gap-6">
              <a
                href={data.download_url ?? undefined}
                target="_blank"
                rel="noreferrer"
                aria-disabled={
                  !data.download_url || isFetching || postGenerateCooldown
                }
                className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition ${
                  data.download_url && !isFetching && !postGenerateCooldown
                    ? "bg-intellend-green text-white hover:bg-emerald-700"
                    : "cursor-not-allowed bg-gray-100 text-gray-400"
                }`}
                onClick={(e) => {
                  if (!data.download_url || isFetching || postGenerateCooldown)
                    e.preventDefault();
                }}
              >
                <Download className="h-4 w-4" />
                Download XLSX
              </a>

              <Button
                type="button"
                variant="outline"
                onClick={handleGenerate}
                disabled={
                  !hasCsv ||
                  isTaskInFlight ||
                  generateMutation.isPending ||
                  isFetching ||
                  postGenerateCooldown
                }
                title={
                  !hasCsv
                    ? "Upload a CSV first"
                    : isTaskInFlight
                      ? "A generation task is already in progress"
                      : undefined
                }
              >
                {generateMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Starting…
                  </>
                ) : (
                  <>
                    <Play className="mr-2 h-4 w-4" />
                    Generate Report
                  </>
                )}
              </Button>
            </div>

            {!hasCsv && !isLoading && (
              <p className="mt-2 text-xs text-gray-400">
                No CSV uploaded yet — upload one below first.
              </p>
            )}

            {generateError && (
              <div className="mt-3 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span className="flex-1">{generateError}</span>
                <button
                  type="button"
                  onClick={() => setGenerateError("")}
                  className="shrink-0 text-red-400 hover:text-red-600"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            )}

            {generateSuccess && (
              <div className="mt-3 flex items-start gap-2 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                {generateSuccess}
              </div>
            )}
          </dl>
        )}
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <h3 className="mb-1 text-base font-semibold text-gray-900">
          Upload Demands CSV
        </h3>
        <p className="mb-4 text-xs text-gray-500">
          Upload the pipe-delimited demands CSV exported from the LMS notebook.
          The file is stored in S3 and can be re-used for multiple report runs.
        </p>

        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">
              Demands CSV (.csv)
            </label>
            <input
              ref={csvRef}
              type="file"
              accept=".csv"
              disabled={isUploading}
              className="text-sm text-gray-600 file:mr-3 file:h-9 file:cursor-pointer file:rounded-md file:border file:border-input file:bg-background file:px-4 file:text-sm file:font-medium file:text-gray-700 hover:file:bg-accent disabled:opacity-50"
            />
          </div>

          <Button
            type="button"
            onClick={handleUpload}
            disabled={isUploading}
            className="mt-1"
          >
            {isUploading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Uploading…
              </>
            ) : (
              <>
                <Upload className="mr-2 h-4 w-4" />
                Upload CSV
              </>
            )}
          </Button>
        </div>

        {uploadError && (
          <div className="mt-3 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span className="flex-1">{uploadError}</span>
            <button
              type="button"
              onClick={() => setUploadError("")}
              className="shrink-0 text-red-400 hover:text-red-600"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        {uploadSuccess && (
          <div className="mt-3 flex items-start gap-2 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
            {uploadSuccess}
          </div>
        )}
      </div>
    </div>
  );
};

export default IntraDayBounceView;
